import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  AvatarGroup,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  LinearProgress,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Business as DepartmentIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as ProjectIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import TeamService, { Team, TeamMember, TeamStats, CreateTeamData, AddTeamMemberData } from '../../../services/TeamService';
import { UsersService, User } from '../../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Teams Management Page
 * Complete team management interface with real API integration
 */
const TeamsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // State for teams and related data
  const [teams, setTeams] = useState<Team[]>([]);
  const [stats, setStats] = useState<TeamStats | null>(null);
  const [departments, setDepartments] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  // Dialog states
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [editTeamDialogOpen, setEditTeamDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [viewTeamDialogOpen, setViewTeamDialogOpen] = useState(false);

  // Form states
  const [teamForm, setTeamForm] = useState<CreateTeamData>({
    name: '',
    description: '',
    department: '',
    team_lead_id: undefined,
    budget: undefined
  });

  const [memberForm, setMemberForm] = useState<AddTeamMemberData>({
    user_id: 0,
    role: '',
    position: '',
    department: ''
  });

  // Filters
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [departmentFilter, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit: 50
      };

      if (departmentFilter !== 'all') {
        params.department = departmentFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Load teams, stats, departments, and users in parallel
      const [teamsResponse, statsData, departmentsData, usersResponse] = await Promise.all([
        TeamService.getTeams(params),
        TeamService.getTeamStats(),
        TeamService.getDepartmentsWithDetails(), // Use the new method that has fallback
        UsersService.getUsers({ limit: 100 })
      ]);

      setTeams(teamsResponse.teams);
      setStats(statsData);
      // Extract department names for the dropdown
      const departmentNames = departmentsData.map(dept => dept.name);
      setDepartments(departmentNames);
      // Filter users to show only potential team leads (exclude clients)
      const eligibleUsers = usersResponse.users.filter(user => 
        ['admin', 'project_manager', 'project_manager_lead', 'developer', 'developer_lead'].includes(user.role)
      );
      setUsers(eligibleUsers);
    } catch (err: any) {
      console.error('Error loading teams data:', err);
      setError('Failed to load teams data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      setError(null);
      const newTeam = await TeamService.createTeam(teamForm);
      setTeams(prev => [...prev, newTeam]);
      setCreateTeamDialogOpen(false);
      resetTeamForm();
      setSuccess('Team created successfully!');
      loadData(); // Reload to update stats
    } catch (err: any) {
      console.error('Error creating team:', err);
      setError(err.message || 'Failed to create team. Please try again.');
    }
  };

  const handleEditTeam = async () => {
    if (!selectedTeam) return;

    try {
      setError(null);
      const updatedTeam = await TeamService.updateTeam(selectedTeam.id, {
        name: teamForm.name,
        description: teamForm.description,
        department: teamForm.department,
        team_lead_id: teamForm.team_lead_id,
        budget: teamForm.budget
      });

      setTeams(prev => prev.map(team => 
        team.id === selectedTeam.id ? updatedTeam : team
      ));
      setEditTeamDialogOpen(false);
      setSelectedTeam(null);
      resetTeamForm();
      setSuccess('Team updated successfully!');
    } catch (err: any) {
      console.error('Error updating team:', err);
      setError(err.message || 'Failed to update team. Please try again.');
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    if (!window.confirm(`Are you sure you want to delete the team "${team.name}"?`)) {
      return;
    }

    try {
      setError(null);
      await TeamService.deleteTeam(teamId);
      setTeams(prev => prev.filter(team => team.id !== teamId));
      setSuccess('Team deleted successfully!');
      loadData(); // Reload to update stats
    } catch (err: any) {
      console.error('Error deleting team:', err);
      setError(err.message || 'Failed to delete team. Please try again.');
    }
  };

  const handleAddMember = async () => {
    if (!selectedTeam) return;

    try {
      setError(null);
      await TeamService.addTeamMember(selectedTeam.id, memberForm);
      setAddMemberDialogOpen(false);
      resetMemberForm();
      setSuccess('Member added to team successfully!');
      
      // Reload the selected team data
      const updatedTeam = await TeamService.getTeam(selectedTeam.id);
      setSelectedTeam(updatedTeam);
      
      // Update teams list
      setTeams(prev => prev.map(team => 
        team.id === selectedTeam.id ? updatedTeam : team
      ));
    } catch (err: any) {
      console.error('Error adding team member:', err);
      setError(err.message || 'Failed to add member to team. Please try again.');
    }
  };

  const handleRemoveMember = async (teamId: number, memberId: number) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    try {
      setError(null);
      await TeamService.removeTeamMember(teamId, memberId);
      setSuccess('Member removed from team successfully!');
      
      // Reload the selected team data if viewing
      if (selectedTeam && selectedTeam.id === teamId) {
        const updatedTeam = await TeamService.getTeam(teamId);
        setSelectedTeam(updatedTeam);
      }
      
      // Update teams list
      loadData();
    } catch (err: any) {
      console.error('Error removing team member:', err);
      setError(err.message || 'Failed to remove member from team. Please try again.');
    }
  };

  const openEditDialog = (team: Team) => {
    setSelectedTeam(team);
    setTeamForm({
      name: team.name,
      description: team.description,
      department: team.department,
      team_lead_id: team.lead?.id,
      budget: team.budget
    });
    setEditTeamDialogOpen(true);
  };

  const openViewDialog = async (team: Team) => {
    try {
      setLoading(true);
      const fullTeamData = await TeamService.getTeam(team.id);
      setSelectedTeam(fullTeamData);
      setViewTeamDialogOpen(true);
    } catch (err: any) {
      console.error('Error loading team details:', err);
      setError('Failed to load team details.');
    } finally {
      setLoading(false);
    }
  };

  const resetTeamForm = () => {
    setTeamForm({
      name: '',
      description: '',
      department: '',
      team_lead_id: undefined,
      budget: undefined
    });
  };

  const resetMemberForm = () => {
    setMemberForm({
      user_id: 0,
      role: '',
      position: '',
      department: ''
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const getPerformanceColor = (score?: number) => {
    if (!score) return 'default';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading && teams.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          <GroupIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Teams Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateTeamDialogOpen(true)}
        >
          Create Team
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Team Statistics */}
      {stats && (
        <Box display="flex" gap={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Teams
              </Typography>
              <Typography variant="h4">
                {stats.total_teams}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Teams
              </Typography>
              <Typography variant="h4">
                {stats.active_teams}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Members
              </Typography>
              <Typography variant="h4">
                {stats.total_members}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Team Size
              </Typography>
              <Typography variant="h4">
                {stats.average_team_size}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              select
              label="Department"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Departments</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Teams Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Team</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Lead</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Performance</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {team.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {team.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<DepartmentIcon />}
                        label={team.department}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {team.lead && team.lead.name ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {team.lead.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">
                            {team.lead.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No lead assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                          {team.members?.slice(0, 3).map((member, idx) => {
                            const memberName = member?.user?.name || `Member ${idx + 1}`;
                            return (
                              <Tooltip key={idx} title={memberName}>
                                <Avatar>
                                  {memberName.charAt(0).toUpperCase()}
                                </Avatar>
                              </Tooltip>
                            );
                          })}
                        </AvatarGroup>
                        <Typography variant="body2">
                          {team.members_count} members
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(team.status || 'unknown').toUpperCase()}
                        color={getStatusColor(team.status || 'unknown') as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {team.performance_score ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={team.performance_score}
                            sx={{ width: 60, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="body2">
                            {team.performance_score}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => openViewDialog(team)}
                        color="primary"
                        size="small"
                      >
                        <GroupIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => openEditDialog(team)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteTeam(team.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {teams.length === 0 && (
            <Box textAlign="center" py={4}>
              <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No teams found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {departmentFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first team to get started'
                }
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create Team Dialog */}
      <Dialog open={createTeamDialogOpen} onClose={() => setCreateTeamDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Team Name"
              value={teamForm.name}
              onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={teamForm.description}
              onChange={(e) => setTeamForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              select
              label="Department"
              value={teamForm.department}
              onChange={(e) => setTeamForm(prev => ({ ...prev, department: e.target.value }))}
              fullWidth
              required
            >
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Team Lead (Optional)"
              value={teamForm.team_lead_id || ''}
              onChange={(e) => setTeamForm(prev => ({ ...prev, team_lead_id: e.target.value ? Number(e.target.value) : undefined }))}
              fullWidth
            >
              <MenuItem value="">No Lead</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Budget (Optional)"
              type="number"
              value={teamForm.budget || ''}
              onChange={(e) => setTeamForm(prev => ({ ...prev, budget: e.target.value ? Number(e.target.value) : undefined }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTeamDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTeam} variant="contained">Create Team</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editTeamDialogOpen} onClose={() => setEditTeamDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Team: {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Team Name"
              value={teamForm.name}
              onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={teamForm.description}
              onChange={(e) => setTeamForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              select
              label="Department"
              value={teamForm.department}
              onChange={(e) => setTeamForm(prev => ({ ...prev, department: e.target.value }))}
              fullWidth
              required
            >
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Team Lead"
              value={teamForm.team_lead_id || ''}
              onChange={(e) => setTeamForm(prev => ({ ...prev, team_lead_id: e.target.value ? Number(e.target.value) : undefined }))}
              fullWidth
            >
              <MenuItem value="">No Lead</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Budget"
              type="number"
              value={teamForm.budget || ''}
              onChange={(e) => setTeamForm(prev => ({ ...prev, budget: e.target.value ? Number(e.target.value) : undefined }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTeamDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditTeam} variant="contained">Update Team</Button>
        </DialogActions>
      </Dialog>

      {/* View Team Details Dialog */}
      <Dialog open={viewTeamDialogOpen} onClose={() => setViewTeamDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Team Details: {selectedTeam?.name}
          <Button
            startIcon={<PersonAddIcon />}
            onClick={() => setAddMemberDialogOpen(true)}
            sx={{ ml: 2 }}
          >
            Add Member
          </Button>
        </DialogTitle>
        <DialogContent>
          {selectedTeam && (
            <Box>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Overview" />
                <Tab label="Members" />
                <Tab label="Performance" />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Box flex="1 1 400px">
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Team Information</Typography>
                        <Typography><strong>Name:</strong> {selectedTeam.name}</Typography>
                        <Typography><strong>Description:</strong> {selectedTeam.description}</Typography>
                        <Typography><strong>Department:</strong> {selectedTeam.department}</Typography>
                        <Typography><strong>Status:</strong> 
                          <Chip 
                            label={(selectedTeam.status || 'unknown').toUpperCase()} 
                            color={getStatusColor(selectedTeam.status || 'unknown') as any}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography><strong>Members:</strong> {selectedTeam.members_count}</Typography>
                        <Typography><strong>Projects:</strong> {selectedTeam.project_count}</Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  <Box flex="1 1 400px">
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Team Lead</Typography>
                        {selectedTeam.lead && selectedTeam.lead.name ? (
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar>{selectedTeam.lead.name.charAt(0).toUpperCase()}</Avatar>
                            <Box>
                              <Typography variant="subtitle1">{selectedTeam.lead.name}</Typography>
                              <Typography variant="body2" color="textSecondary">{selectedTeam.lead.email}</Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography color="textSecondary">No team lead assigned</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <List>
                  {selectedTeam.members?.map((member) => {
                    const memberName = member?.user?.name || 'Unknown Member';
                    const memberEmail = member?.user?.email || 'No email';
                    return (
                      <ListItem key={member.id} divider>
                        <ListItemAvatar>
                          <Avatar>{memberName.charAt(0).toUpperCase()}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={memberName}
                          secondary={
                            <Box>
                              <Typography variant="body2">{memberEmail}</Typography>
                              <Typography variant="body2">
                                {member.role} - {member.position} ({member.department})
                              </Typography>
                              <Chip 
                                label={(member.status || 'unknown').toUpperCase()} 
                                color={getStatusColor(member.status || 'unknown') as any}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveMember(selectedTeam.id, member.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
                {(!selectedTeam.members || selectedTeam.members.length === 0) && (
                  <Box textAlign="center" py={4}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      No members in this team
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Add members to get started
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                    {selectedTeam.performance_score ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Typography>Overall Performance:</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={selectedTeam.performance_score}
                            sx={{ width: 200, height: 8, borderRadius: 4 }}
                          />
                          <Typography>{selectedTeam.performance_score}%</Typography>
                        </Box>
                        <Typography color="textSecondary">
                          Performance metrics are calculated based on project completion rates, 
                          quality scores, and team collaboration metrics.
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="textSecondary">
                        No performance data available yet. Performance metrics will appear 
                        as the team completes projects and activities.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </TabPanel>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTeamDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member to {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              select
              label="User"
              value={memberForm.user_id}
              onChange={(e) => setMemberForm(prev => ({ ...prev, user_id: Number(e.target.value) }))}
              fullWidth
              required
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Role"
              value={memberForm.role}
              onChange={(e) => setMemberForm(prev => ({ ...prev, role: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Developer, Designer, Tester"
            />
            <TextField
              label="Position"
              value={memberForm.position}
              onChange={(e) => setMemberForm(prev => ({ ...prev, position: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Senior Developer, Junior Designer"
            />
            <TextField
              select
              label="Department"
              value={memberForm.department}
              onChange={(e) => setMemberForm(prev => ({ ...prev, department: e.target.value }))}
              fullWidth
              required
            >
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained">Add Member</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeamsPage;